import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminService } from '../../../services/adminService';

// Owns all state + handlers for the Document Templates page: list loading,
// create/edit/preview/publish/delete dialogs and their submit flows.
export const useDocumentTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Dialogs
  const [editDialog, setEditDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [publishDialog, setPublishDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  // Active template + edit form
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editExpiresInDays, setEditExpiresInDays] = useState('');
  // Create form
  const [createTitle, setCreateTitle] = useState('');
  const [createDocType, setCreateDocType] = useState('custom');
  const [createContent, setCreateContent] = useState('');
  const [createExpiresInDays, setCreateExpiresInDays] = useState('365');
  // Publish
  const [publishMode, setPublishMode] = useState('all');
  const [selectedClients, setSelectedClients] = useState([]);
  const [addendumSummary, setAddendumSummary] = useState('');
  // Loading flags
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getDocumentTemplates();
      setTemplates(data);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      const data = await adminService.getClients();
      setClients(data.filter((c) => c.status === 'active'));
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadClients();
  }, [loadTemplates, loadClients]);

  // ─── Open dialogs ──────────────────────
  const openCreate = () => {
    setCreateTitle('');
    setCreateDocType('custom');
    setCreateContent('<h2>Document Title</h2><p>Enter your document content here.</p>');
    setCreateExpiresInDays('365');
    setCreateDialog(true);
  };

  const openEdit = (template) => {
    setActiveTemplate(template);
    setEditTitle(template.title);
    setEditContent(template.content);
    setEditExpiresInDays(template.expires_in_days ? String(template.expires_in_days) : '');
    setEditDialog(true);
  };

  const openPreview = (template) => {
    setActiveTemplate(template);
    setPreviewDialog(true);
  };

  const openPublish = (template) => {
    setActiveTemplate(template);
    setPublishMode('all');
    setSelectedClients([]);
    setAddendumSummary('');
    setPublishDialog(true);
  };

  const openDelete = (template) => {
    setActiveTemplate(template);
    setDeleteDialog(true);
  };

  // ─── Submit flows ──────────────────────
  const handleCreate = async () => {
    if (!createTitle.trim() || !createContent.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setCreating(true);
    try {
      await adminService.createDocumentTemplate({
        title: createTitle,
        doc_type: createDocType,
        content: createContent,
        expires_in_days: createExpiresInDays && createExpiresInDays !== 'none' ? parseInt(createExpiresInDays, 10) : null,
      });
      toast.success('Document template created');
      setCreateDialog(false);
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create template');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!editTitle.trim() || !editContent.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      await adminService.updateDocumentTemplate(activeTemplate.id, {
        title: editTitle,
        content: editContent,
        expires_in_days: editExpiresInDays && editExpiresInDays !== 'none' ? parseInt(editExpiresInDays, 10) : 0,
      });
      toast.success('Template saved (version incremented)');
      setEditDialog(false);
      loadTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!activeTemplate) return;
    const clientIds = publishMode === 'all' ? ['all'] : selectedClients;
    if (publishMode === 'select' && clientIds.length === 0) {
      toast.error('Select at least one client');
      return;
    }
    setPublishing(true);
    try {
      const result = await adminService.publishDocumentTemplate(
        activeTemplate.id,
        clientIds,
        addendumSummary || null
      );
      toast.success(
        `Published to ${result.total_clients} client(s). ${result.addendums_created} addendum(s) created.`
      );
      setPublishDialog(false);
    } catch (err) {
      toast.error('Failed to publish template');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!activeTemplate) return;
    setDeleting(true);
    try {
      await adminService.deleteDocumentTemplate(activeTemplate.id);
      toast.success('Template deleted');
      setDeleteDialog(false);
      loadTemplates();
    } catch (err) {
      // If has references, try archive
      if (err?.response?.status === 400) {
        try {
          await adminService.archiveDocumentTemplate(activeTemplate.id);
          toast.success('Template archived (has published documents)');
          setDeleteDialog(false);
          loadTemplates();
        } catch (archErr) {
          toast.error('Failed to archive template');
        }
      } else {
        toast.error('Failed to delete template');
      }
    } finally {
      setDeleting(false);
    }
  };

  const toggleClientSelect = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  return {
    templates,
    clients,
    loading,
    activeTemplate,
    // dialogs
    editDialog,
    setEditDialog,
    createDialog,
    setCreateDialog,
    previewDialog,
    setPreviewDialog,
    publishDialog,
    setPublishDialog,
    deleteDialog,
    setDeleteDialog,
    // create form
    createTitle,
    setCreateTitle,
    createDocType,
    setCreateDocType,
    createContent,
    setCreateContent,
    createExpiresInDays,
    setCreateExpiresInDays,
    creating,
    // edit form
    editTitle,
    setEditTitle,
    editContent,
    setEditContent,
    editExpiresInDays,
    setEditExpiresInDays,
    saving,
    // publish
    publishMode,
    setPublishMode,
    selectedClients,
    addendumSummary,
    setAddendumSummary,
    publishing,
    toggleClientSelect,
    // delete
    deleting,
    // openers + handlers
    openCreate,
    openEdit,
    openPreview,
    openPublish,
    openDelete,
    handleCreate,
    handleSave,
    handlePublish,
    handleDelete,
  };
};
